/**
 * Splitter — the 6px draggable divider between two review columns: a
 * hairline that thickens to 3px on hover/drag plus a 4×30px grip. It owns the pointer capture and the keyboard affordance; the widths
 * themselves live in `useResizableColumns`.
 */
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react';

import { cn } from '../../../../../lib/utils';
import { FOCUS_TOGGLE_SELECTOR } from './focusToggleAttribute';
import { KEYBOARD_RESIZE_STEP_PX, MIN_COLUMN_PX, type ColumnKind } from './useResizableColumns';

const HAIRLINE_CLASSES = 'pointer-events-none absolute inset-y-0 left-[2px] w-px bg-border transition-all duration-150';
const HAIRLINE_ACTIVE_CLASSES = 'left-[1px] w-[3px] bg-foreground';
// Hovering the collapse toggle is not hovering the splitter, so the hairline
// and grip fall back to rest.
// Tailwind's arbitrary-variant selector must stay a static string for the
// build-time scanner to pick it up — it cannot be built from
// FOCUS_TOGGLE_ATTRIBUTE at runtime, but the attribute name below must keep
// matching it (and the matching selector in the grip's className further down).
const HAIRLINE_TOGGLE_HOVER_CLASSES = 'group-has-[[data-focus-toggle]:hover]:left-[2px] group-has-[[data-focus-toggle]:hover]:w-px group-has-[[data-focus-toggle]:hover]:bg-border';
const GRIP_CLASSES =
  'pointer-events-none absolute left-1/2 top-1/2 h-[30px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-[3px] bg-transparent transition-all duration-150';

export interface SplitterProps {
  column: ColumnKind;
  /** Current width of the column this splitter sizes — announced to AT. */
  widthPx: number;
  /** Widest that column may currently be, from the same clamp the drag uses. */
  maxWidthPx: number;
  label: string;
  dragging: boolean;
  /** Focus mode: the first splitter stays visible but no longer resizes. */
  disabled?: boolean;
  /** Its column is collapsed to 0px — clip the bar away and stop resizing. */
  collapsed?: boolean;
  onResizeStart(clientX: number): void;
  /** Returns the column's live width mid-drag, which the bar announces before the release commits it. */
  onResizeMove(clientX: number): number | undefined;
  onResizeEnd(): void;
  onReset(): void;
  /** Keyboard resize; positive deltas move the pointer (and the divider) right. */
  onNudge(deltaPx: number): void;
  children?: ReactNode;
}

function isFocusToggleEvent(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(FOCUS_TOGGLE_SELECTOR) !== null;
}

/** jsdom and older Safari have no pointer capture; dragging still works without it. */
function capturePointer(element: Element, pointerId: number): void {
  if (typeof element.setPointerCapture !== 'function') return;
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // A pointer that has already been released cannot be captured — harmless.
  }
}

export function Splitter({ column, widthPx, maxWidthPx, label, dragging, disabled = false, collapsed = false, onResizeStart, onResizeMove, onResizeEnd, onReset, onNudge, children }: SplitterProps) {
  const interactive = !disabled && !collapsed;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!interactive || isFocusToggleEvent(event.target)) return;
    event.preventDefault();
    capturePointer(event.currentTarget, event.pointerId);
    onResizeStart(event.clientX);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const liveWidthPx = onResizeMove(event.clientX);
    // A drag doesn't re-render until the release, so the live width goes straight onto the bar.
    if (liveWidthPx === undefined) return;
    event.currentTarget.setAttribute('aria-valuenow', String(liveWidthPx));
  };

  const handleDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!interactive || isFocusToggleEvent(event.target)) return;
    onReset();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // No `defaultPrevented` guard: the review shortcut map ignores the arrows
    // on `role="separator"`, so a nudge here never also steps the video.
    if (!interactive) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    onNudge(event.key === 'ArrowRight' ? KEYBOARD_RESIZE_STEP_PX : -KEYBOARD_RESIZE_STEP_PX);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={widthPx}
      aria-valuemin={MIN_COLUMN_PX}
      aria-valuemax={maxWidthPx}
      aria-disabled={!interactive || undefined}
      tabIndex={interactive ? 0 : -1}
      data-column-splitter={column}
      className={cn(
        'group relative z-[6] touch-none outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand-500)',
        interactive ? 'cursor-col-resize' : 'cursor-default',
        collapsed && 'overflow-hidden',
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={onResizeEnd}
      onPointerCancel={onResizeEnd}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
    >
      <span
        aria-hidden
        className={cn(
          HAIRLINE_CLASSES,
          interactive && 'group-hover:left-[1px] group-hover:w-[3px] group-hover:bg-foreground',
          dragging && HAIRLINE_ACTIVE_CLASSES,
          HAIRLINE_TOGGLE_HOVER_CLASSES,
        )}
      />
      {interactive && (
        <span
          aria-hidden
          className={cn(
            GRIP_CLASSES,
            'group-hover:bg-foreground group-has-[[data-focus-toggle]:hover]:bg-transparent',
            dragging && 'bg-foreground',
          )}
        />
      )}
      {children}
    </div>
  );
}
